import {
  HbrDropdown,
  HbrIcon,
  HbrInput,
  HbrMenu,
  HbrMenuItem,
} from '@harbor/elements-react-standalone';
import {
  dotsThreeVertical,
  trash,
  arrowLeft,
  arrowRight,
  magnifyingGlass,
} from '@harbor/elements/icons';
import React, { useMemo } from 'react';
import { Column, getColumnTypeForPermission } from '../../../types/table';
import { useSelector } from 'react-redux';
import { selectSalesforcePermissionById } from '../../../support/slices/salesforcePermissionsSlice';
import { RootState } from '../../../support/store';
import Highlighter from 'react-highlight-words';

interface Props {
  column: Column;
  selectedColumns: Column[];
  setColumns: (columns: Column[]) => void;
  permissionsId: string;
}

type UiState = 'default' | 'addLeft' | 'addRight';

const StbColumnManager = ({ column, selectedColumns, setColumns, permissionsId }: Props) => {
  const [uiState, setUiState] = React.useState<UiState>('default');
  const [search, setSearch] = React.useState<string>('');
  const permissions = useSelector((state: RootState) =>
    selectSalesforcePermissionById(state, permissionsId)
  );

  const availableColumns = useMemo(() => {
    return (permissions ?? [])
      .map(
        (permission) =>
          ({
            id: permission.value,
            name: permission.name,
            columnType: getColumnTypeForPermission(permission),
            sortable: !!(permission?.source === 'salesforce'),
          }) as Column
      )
      .filter(Boolean)
      .filter((col) => !selectedColumns.map((col) => col.id).includes(col.id))
      .sort((a, b) =>
        (a.name ?? '').toLocaleLowerCase().localeCompare((b.name ?? '').toLocaleLowerCase())
      );
  }, [permissions, selectedColumns]);

  const columnPosition = useMemo(() => {
    return selectedColumns.findIndex((c) => c.id === column.id);
  }, [selectedColumns, column]);

  const handleAddColumn = (columnId: string): void => {
    const position: number =
      uiState === 'addLeft' ? columnPosition : columnPosition + 1;
    const newColumnList = [...selectedColumns];
    const columnConfig = availableColumns.find(
      (c: Column) => c.id === columnId
    );
    if (columnConfig) {
      newColumnList.splice(position, 0, columnConfig);
      setColumns(newColumnList);
    }
    setUiState('default');
  };

  const handleRemoveColumn = (): void => {
    const newColumnList = selectedColumns.filter(
      (c: Column) => c.id !== column.id
    );
    setColumns(newColumnList);
    setUiState('default');
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const searchfilter = (column: Column) => {
    if (!search.trim()) return true;
    // Use ?. and provide a fallback empty string before calling toLowerCase
    return (column.name ?? '').toLowerCase().includes(search.toLowerCase());
  };

  return (
    <>
      <HbrDropdown onClick={(e) => e.stopPropagation()}>
        <HbrIcon
          asset={dotsThreeVertical}
          slot='trigger'
          className='text-2xl opacity-40 hover:opacity-100 -ml-2 -mr-1 p-1'
          onClick={() => setUiState('default')}
        />

        {uiState === 'default' && (
          <HbrMenu className='absolute transition-all -right-4'>
            <h5 className='mx-3 my-1'>{column.name}</h5>
            <HbrMenuItem
              value='remove'
              className='truncate w-64'
              onClick={handleRemoveColumn}
            >
              <HbrIcon asset={trash} slot='prefix' />
              Remove this column
            </HbrMenuItem>
            <HbrMenuItem
              value='addLeft'
              className='w-64'
              onClick={(e) => {
                e.stopPropagation();
                setUiState('addLeft');
              }}
            >
              <HbrIcon asset={arrowLeft} slot='prefix' />
              Add column (left)
            </HbrMenuItem>
            <HbrMenuItem
              value='addRight'
              onClick={(e) => {
                e.stopPropagation();
                setUiState('addRight');
              }}
              className='w-64'
            >
              <HbrIcon asset={arrowRight} slot='prefix' />
              Add column (right)
            </HbrMenuItem>
          </HbrMenu>
        )}
        {['addLeft', 'addRight'].includes(uiState) && (
          <div className="absolute top-0 -right-4">
            <div className='rounded-t-md -mb-1 rounded-b-none overflow-y-hidden sticky top-0 z-10 p-2 bg-gray-200 border-gray-500 border-2'>
              <h5 className='mb-2'>Select column</h5>
              <HbrInput
                placeholder='Search columns'
                value={search}
                size='medium'
                clearable={true}
                onHbr-change={handleSearchChange}
                onKeyDown={(e) => {e.stopPropagation();handleSearchChange(e);}}
                className='w-full mb-1 font-semibold'
              >
                <HbrIcon
                  slot='prefix'
                  name='magnifying-glass'
                  asset={magnifyingGlass}
                  size='16px'
                  staticColors={true}
                />
              </HbrInput>
            </div>
            <HbrMenu className='z-0'>
              <div className='min-w-64'></div>
              {availableColumns?.filter(searchfilter)?.map((column: Column, n: number) => (
                <HbrMenuItem
                  value={column.id}
                  key={`column-manager-option-${column.id}-${n}`}
                  className=''
                  onClick={() => {
                    handleAddColumn(column.id);
                  }}
                >
                  <Highlighter
                    highlightClassName='font-semibold p-px -m-px rounded-sm bg-warning-lighter'
                    searchWords={[search]}
                    autoEscape={true}
                    textToHighlight={column.name}
                  />
                </HbrMenuItem>
              ))}
              {!availableColumns?.filter(searchfilter)?.length && (
                <div className='p-4 text-center text-gray-500 italic'>
                  No columns found
                </div>
              )}
            </HbrMenu>
          </div>
        )}
      </HbrDropdown>
    </>
  );
};

export default StbColumnManager;
